/**
 * Header search bar in micasa style.
 *
 * - Always visible search field in the header (full width row on mobile).
 * - On focus a panel opens with recent searches and popular search terms.
 * - While typing, predictive results (suggestions, categories, pages, products)
 *   are loaded from Shopify's /search/suggest.json endpoint.
 * - Arrow keys / Enter / Escape keyboard navigation, overlay dims the page.
 */

const RECENT_KEY = 'kauer-recent-searches';
const RECENT_MAX = 6;
const DEBOUNCE_MS = 250;

const ICON_SEARCH =
  '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="4.75" stroke="currentColor" stroke-width="1.5"/><path stroke="currentColor" stroke-linecap="round" stroke-width="1.5" d="m12.5 12.5 3.25 3.25"/></svg>';
const ICON_CLOCK =
  '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="6.75" stroke="currentColor" stroke-width="1.5"/><path stroke="currentColor" stroke-linecap="round" stroke-width="1.5" d="M10 6.5V10l2.5 1.5"/></svg>';
const ICON_ARROW =
  '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m8 5 5 5-5 5"/></svg>';

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Highlights the search term inside a text.
 * @param {string} text
 * @param {string} term
 * @returns {string}
 */
function highlight(text, term) {
  const safe = escapeHtml(text);
  const needle = term.trim();
  if (!needle) return safe;

  const pattern = new RegExp(`(${escapeHtml(needle).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
  return safe.replace(pattern, '<mark>$1</mark>');
}

/**
 * @param {string} url
 * @param {number} width
 * @returns {string}
 */
function resizeImage(url, width) {
  if (!url) return '';
  try {
    const imageUrl = new URL(url, location.origin);
    imageUrl.searchParams.set('width', String(width));
    return imageUrl.toString();
  } catch {
    return url;
  }
}

class KauerSearchBar extends HTMLElement {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {AbortController} */
  #listeners = new AbortController();

  /** @type {number | undefined} */
  #debounceTimer;

  /** @type {number} */
  #activeIndex = -1;

  /** @type {string} */
  #currentTerm = '';

  #lastScrollY = 0;

  #collapseLockUntil = 0;

  connectedCallback() {
    this.input = /** @type {HTMLInputElement} */ (this.querySelector('[data-kauer-search-input]'));
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form'));
    this.panel = /** @type {HTMLElement} */ (this.querySelector('[data-kauer-search-panel]'));
    this.content = /** @type {HTMLElement} */ (this.querySelector('[data-kauer-search-content]'));
    this.status = this.querySelector('[data-kauer-search-status]');
    this.resetButton = /** @type {HTMLElement} */ (this.querySelector('[data-kauer-search-reset]'));
    this.spinner = this.querySelector('[data-kauer-search-spinner]');
    this.closeButton = this.querySelector('[data-kauer-search-close]');
    this.header = this.closest('#header-component') || document.querySelector('#header-component');
    this.toggleButton = this.header?.querySelector('[data-kauer-search-toggle]');
    this.overlay = this.header?.querySelector('[data-kauer-search-overlay]') || null;

    if (!this.input || !this.form || !this.panel || !this.content) return;

    // The header uses `contain: layout`, which would trap fixed-positioned children.
    // Move the panel and overlay to <body> so they can cover the page.
    document.body.append(this.panel);
    if (this.overlay) document.body.append(this.overlay);

    const { signal } = this.#listeners;

    this.input.addEventListener('focus', () => this.open(), { signal });
    this.input.addEventListener('click', () => this.open(), { signal });
    this.input.addEventListener('input', () => this.#onInput(), { signal });
    this.input.addEventListener('keydown', (event) => this.#onKeyDown(event), { signal });
    this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    this.form.addEventListener('reset', (event) => this.#onReset(event), { signal });
    this.closeButton?.addEventListener('click', () => this.close(), { signal });
    this.overlay?.addEventListener('click', () => this.close(), { signal });
    this.toggleButton?.addEventListener(
      'click',
      () => {
        this.input.focus();
        this.open();
      },
      { signal }
    );

    this.panel.addEventListener('click', (event) => this.#onPanelClick(event), { signal });
    this.panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.input.focus();
      }
    }, { signal });

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.isOpen) return;
        const target = /** @type {Node} */ (event.target);
        if (this.contains(target) || this.panel.contains(target) || this.toggleButton?.contains(target)) return;
        this.close();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      },
      { signal }
    );

    const reposition = () => {
      if (this.isOpen) this.#position();
    };
    window.addEventListener('resize', reposition, { signal });
    window.addEventListener('scroll', reposition, { signal, passive: true });
    window.addEventListener('scroll', () => this.#updateCollapsed(), { signal, passive: true });

    this.#toggleResetButton();
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#fetchController?.abort();
    window.clearTimeout(this.#debounceTimer);
    document.body.classList.remove('kauer-search-open');
    this.panel?.remove();
    this.overlay?.remove();
  }

  /**
   * Mobile: hide the search row while scrolling down, show it again when scrolling up.
   * Uses its own scroll tracking with a short lock, because collapsing the row changes the
   * page height (scroll anchoring) which would otherwise read as scrolling up and flicker.
   */
  #updateCollapsed() {
    const y = window.scrollY;

    if (!window.matchMedia('(max-width: 749px)').matches || this.isOpen) {
      this.classList.remove('is-collapsed');
      this.#lastScrollY = y;
      return;
    }

    if (performance.now() < this.#collapseLockUntil) {
      this.#lastScrollY = y;
      return;
    }

    const delta = y - this.#lastScrollY;
    if (Math.abs(delta) < 10 && y > 80) return;

    const collapse = y > 80 && delta > 0;
    this.#lastScrollY = y;

    if (collapse !== this.classList.contains('is-collapsed')) {
      this.classList.toggle('is-collapsed', collapse);
      this.#collapseLockUntil = performance.now() + 400;
    }
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  get resultsLimit() {
    return Number(this.dataset.resultsLimit) || 10;
  }

  get popularTerms() {
    return (this.dataset.popularTerms || '')
      .split(',')
      .map((term) => term.trim())
      .filter(Boolean);
  }

  open() {
    if (!this.isOpen) {
      this.classList.add('is-open');
      document.body.classList.add('kauer-search-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
    }

    this.#position();

    const term = this.input.value.trim();
    if (term) {
      if (term !== this.#currentTerm) this.#search(term);
    } else {
      this.#renderEmptyState();
    }
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    document.body.classList.remove('kauer-search-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.panel.hidden = true;
    this.#activeIndex = -1;
    this.#fetchController?.abort();
    this.#setLoading(false);
    if (document.activeElement === this.input) this.input.blur();
  }

  /** Places the panel directly under the search field and the overlay under the header. */
  #position() {
    const field = this.querySelector('.kauer-search__field') || this.input;
    const rect = field.getBoundingClientRect();
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const margin = 16;
    const viewport = document.documentElement.clientWidth;

    if (isMobile) {
      const headerBottom = this.header?.getBoundingClientRect().bottom ?? rect.bottom;
      this.panel.style.setProperty('--kauer-panel-top', `${Math.max(rect.bottom + 12, headerBottom)}px`);
      this.panel.style.removeProperty('--kauer-panel-left');
      this.panel.style.removeProperty('--kauer-panel-width');
    } else {
      const width = Math.min(Math.max(rect.width, 760), viewport - margin * 2);
      // Align to the field, but keep the panel inside the viewport
      let left = rect.left + rect.width / 2 - width / 2;
      left = Math.min(Math.max(left, margin), viewport - width - margin);

      this.panel.style.setProperty('--kauer-panel-top', `${rect.bottom + 8}px`);
      this.panel.style.setProperty('--kauer-panel-left', `${left}px`);
      this.panel.style.setProperty('--kauer-panel-width', `${width}px`);
    }

    if (this.overlay) {
      const headerBottom = this.header?.getBoundingClientRect().bottom ?? 0;
      this.overlay.style.setProperty('--kauer-overlay-top', `${Math.max(headerBottom, 0)}px`);
    }
  }

  #onInput() {
    this.#toggleResetButton();
    if (!this.isOpen) this.open();

    window.clearTimeout(this.#debounceTimer);
    const term = this.input.value.trim();

    if (!term) {
      this.#fetchController?.abort();
      this.#setLoading(false);
      this.#currentTerm = '';
      this.#renderEmptyState();
      return;
    }

    this.#debounceTimer = window.setTimeout(() => this.#search(term), DEBOUNCE_MS);
  }

  /** @param {SubmitEvent} event */
  #onSubmit(event) {
    const term = this.input.value.trim();
    if (!term) {
      event.preventDefault();
      this.input.focus();
      return;
    }
    this.#saveRecent(term);
  }

  /** @param {Event} event */
  #onReset(event) {
    event.preventDefault();
    this.input.value = '';
    this.#toggleResetButton();
    this.#fetchController?.abort();
    this.#setLoading(false);
    this.#currentTerm = '';
    this.input.focus();
    this.open();
  }

  /** @param {MouseEvent} event */
  #onPanelClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-kauer-clear-recent]')) {
      event.preventDefault();
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch {
        // storage unavailable
      }
      this.#renderEmptyState();
      this.input.focus();
      return;
    }

    const termLink = target.closest('[data-kauer-term]');
    if (termLink instanceof HTMLElement && termLink.dataset.kauerTerm) {
      this.#saveRecent(termLink.dataset.kauerTerm);
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown(event) {
    const items = this.#items;

    switch (event.key) {
      case 'ArrowDown':
        if (!this.isOpen) this.open();
        if (!items.length) return;
        event.preventDefault();
        this.#setActive(this.#activeIndex < items.length - 1 ? this.#activeIndex + 1 : 0);
        break;
      case 'ArrowUp':
        if (!items.length) return;
        event.preventDefault();
        this.#setActive(this.#activeIndex > 0 ? this.#activeIndex - 1 : items.length - 1);
        break;
      case 'Enter': {
        const active = items[this.#activeIndex];
        if (active instanceof HTMLAnchorElement) {
          event.preventDefault();
          if (active.dataset.kauerTerm) this.#saveRecent(active.dataset.kauerTerm);
          window.location.href = active.href;
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        if (this.input.value) {
          this.form.reset();
        } else {
          this.close();
        }
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  get #items() {
    return /** @type {HTMLElement[]} */ (Array.from(this.panel.querySelectorAll('[data-kauer-item]')));
  }

  /** @param {number} index */
  #setActive(index) {
    const items = this.#items;
    items.forEach((item, i) => {
      const active = i === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', String(active));
    });

    this.#activeIndex = index;
    const active = items[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  #toggleResetButton() {
    this.resetButton.hidden = this.input.value.length === 0;
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    this.resetButton.classList.toggle('is-loading', loading);
    if (this.spinner instanceof HTMLElement) this.spinner.hidden = !loading;
  }

  /** @param {string} term */
  #searchUrl(term) {
    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('options[prefix]', 'last');
    return url.pathname + url.search;
  }

  /** @returns {string[]} */
  #getRecent() {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter((value) => typeof value === 'string') : [];
    } catch {
      return [];
    }
  }

  /** @param {string} term */
  #saveRecent(term) {
    const value = term.trim();
    if (!value) return;

    const recent = this.#getRecent().filter((item) => item.toLowerCase() !== value.toLowerCase());
    recent.unshift(value);

    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, RECENT_MAX)));
    } catch {
      // storage unavailable
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #formatMoney(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const currency = window.Shopify?.currency?.active || this.dataset.currency || 'CHF';
    try {
      return new Intl.NumberFormat('de-CH', { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  #renderEmptyState() {
    this.#activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    let itemId = 0;
    const nextId = () => `${this.panel.id}-item-${itemId++}`;

    const recent = this.#getRecent();
    const popular = this.popularTerms;
    let html = '';

    if (recent.length) {
      html += `
        <div class="kauer-search__group">
          <p class="kauer-search__heading">
            <span>Zuletzt gesucht</span>
            <button type="button" data-kauer-clear-recent>Verlauf löschen</button>
          </p>
          <ul class="kauer-search__list" role="listbox" aria-label="Zuletzt gesucht">
            ${recent
              .map(
                (term) => `
              <li role="presentation">
                <a class="kauer-search__link" id="${nextId()}" role="option" data-kauer-item data-kauer-term="${escapeHtml(term)}" href="${this.#searchUrl(term)}">
                  ${ICON_CLOCK}<span>${escapeHtml(term)}</span>
                </a>
              </li>`
              )
              .join('')}
          </ul>
        </div>`;
    }

    if (popular.length) {
      html += `
        <div class="kauer-search__group">
          <p class="kauer-search__heading"><span>Beliebte Suchbegriffe</span></p>
          <ul class="kauer-search__pills" role="listbox" aria-label="Beliebte Suchbegriffe">
            ${popular
              .map(
                (term) => `
              <li role="presentation">
                <a class="kauer-search__pill" id="${nextId()}" role="option" data-kauer-item data-kauer-term="${escapeHtml(term)}" href="${this.#searchUrl(term)}">${escapeHtml(term)}</a>
              </li>`
              )
              .join('')}
          </ul>
        </div>`;
    }

    if (!html) {
      html = '<p class="kauer-search__empty">Gib einen Suchbegriff ein, z. B. ein Produkt oder eine Kategorie.</p>';
    }

    this.content.innerHTML = html;
    this.panel.scrollTop = 0;
  }

  /** @param {string} term */
  async #search(term) {
    this.#fetchController?.abort();
    const controller = new AbortController();
    this.#fetchController = controller;
    this.#currentTerm = term;
    this.#setLoading(true);

    const url = new URL(`${this.dataset.suggestUrl || '/search/suggest'}.json`, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query,product,collection,page');
    url.searchParams.set('resources[limit]', String(this.resultsLimit));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      if (controller.signal.aborted) return;
      this.#renderResults(term, data?.resources?.results || {});
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error(error);
      this.content.innerHTML = `
        <p class="kauer-search__empty">Die Vorschläge konnten nicht geladen werden.</p>
        <div class="kauer-search__footer">
          <a class="kauer-search__all" href="${this.#searchUrl(term)}">Nach „${escapeHtml(term)}“ suchen</a>
        </div>`;
    } finally {
      if (this.#fetchController === controller) this.#setLoading(false);
    }
  }

  /**
   * @param {string} term
   * @param {{queries?: any[], products?: any[], collections?: any[], pages?: any[]}} results
   */
  #renderResults(term, results) {
    this.#activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    let itemId = 0;
    const nextId = () => `${this.panel.id}-item-${itemId++}`;

    const queries = (results.queries || []).slice(0, 6);
    const collections = (results.collections || []).slice(0, 5);
    const pages = (results.pages || []).slice(0, 3);
    const products = (results.products || []).slice(0, this.resultsLimit);

    const total = queries.length + collections.length + pages.length + products.length;

    if (!total) {
      this.content.innerHTML = `
        <p class="kauer-search__empty">Keine Treffer für „<strong>${escapeHtml(term)}</strong>“. Überprüfe die Schreibweise oder versuche einen anderen Suchbegriff.</p>
        ${this.#popularGroupHtml(nextId)}`;
      if (this.status) this.status.textContent = `Keine Ergebnisse für ${term}`;
      return;
    }

    let aside = '';

    if (queries.length) {
      aside += `
        <div class="kauer-search__group">
          <p class="kauer-search__heading"><span>Suchvorschläge</span></p>
          <ul class="kauer-search__list" role="listbox" aria-label="Suchvorschläge">
            ${queries
              .map(
                (query) => `
              <li role="presentation">
                <a class="kauer-search__link" id="${nextId()}" role="option" data-kauer-item data-kauer-term="${escapeHtml(query.text)}" href="${escapeHtml(query.url || this.#searchUrl(query.text))}">
                  ${ICON_SEARCH}<span>${highlight(query.text, term)}</span>
                </a>
              </li>`
              )
              .join('')}
          </ul>
        </div>`;
    }

    if (collections.length) {
      aside += `
        <div class="kauer-search__group">
          <p class="kauer-search__heading"><span>Kategorien</span></p>
          <ul class="kauer-search__list" role="listbox" aria-label="Kategorien">
            ${collections
              .map(
                (collection) => `
              <li role="presentation">
                <a class="kauer-search__link" id="${nextId()}" role="option" data-kauer-item href="${escapeHtml(collection.url)}">
                  <span>${highlight(collection.title, term)}</span>${ICON_ARROW}
                </a>
              </li>`
              )
              .join('')}
          </ul>
        </div>`;
    }

    if (pages.length) {
      aside += `
        <div class="kauer-search__group">
          <p class="kauer-search__heading"><span>Seiten</span></p>
          <ul class="kauer-search__list" role="listbox" aria-label="Seiten">
            ${pages
              .map(
                (page) => `
              <li role="presentation">
                <a class="kauer-search__link" id="${nextId()}" role="option" data-kauer-item href="${escapeHtml(page.url)}">
                  <span>${highlight(page.title, term)}</span>${ICON_ARROW}
                </a>
              </li>`
              )
              .join('')}
          </ul>
        </div>`;
    }

    let main = '';

    if (products.length) {
      main = `
        <div class="kauer-search__group">
          <p class="kauer-search__heading"><span>Produkte</span></p>
          <ul class="kauer-search__products" role="listbox" aria-label="Produkte">
            ${products.map((product) => this.#productHtml(product, term, nextId())).join('')}
          </ul>
        </div>`;
    }

    const gridClass = aside && main ? 'kauer-search__grid' : 'kauer-search__grid kauer-search__grid--single';

    this.content.innerHTML = `
      <div class="${gridClass}">
        ${aside ? `<div>${aside}</div>` : ''}
        ${main ? `<div>${main}</div>` : ''}
      </div>
      <div class="kauer-search__footer">
        <a class="kauer-search__all" id="${nextId()}" data-kauer-item data-kauer-term="${escapeHtml(term)}" href="${this.#searchUrl(term)}">
          Alle Ergebnisse für „${escapeHtml(term)}“ anzeigen
        </a>
      </div>`;

    this.panel.scrollTop = 0;
    if (this.status) {
      this.status.textContent = `${products.length} Produkte und ${queries.length} Suchvorschläge für ${term}`;
    }
  }

  /**
   * @param {() => string} nextId
   * @returns {string}
   */
  #popularGroupHtml(nextId) {
    const popular = this.popularTerms;
    if (!popular.length) return '';

    return `
      <div class="kauer-search__group">
        <p class="kauer-search__heading"><span>Beliebte Suchbegriffe</span></p>
        <ul class="kauer-search__pills" role="listbox" aria-label="Beliebte Suchbegriffe">
          ${popular
            .map(
              (item) => `
            <li role="presentation">
              <a class="kauer-search__pill" id="${nextId()}" role="option" data-kauer-item data-kauer-term="${escapeHtml(item)}" href="${this.#searchUrl(item)}">${escapeHtml(item)}</a>
            </li>`
            )
            .join('')}
        </ul>
      </div>`;
  }

  /**
   * @param {any} product
   * @param {string} term
   * @param {string} id
   * @returns {string}
   */
  #productHtml(product, term, id) {
    const image = product.featured_image?.url || product.image || '';
    const alt = product.featured_image?.alt || product.title;
    const price = Number(product.price);
    const compareAt = Number(product.compare_at_price_max);
    const onSale = Number.isFinite(compareAt) && compareAt > price;
    const priceVaries = product.price_min && product.price_max && product.price_min !== product.price_max;

    return `
      <li role="presentation">
        <a class="kauer-search__product" id="${id}" role="option" data-kauer-item href="${escapeHtml(product.url)}">
          <span class="kauer-search__product-image">
            ${
              image
                ? `<img src="${escapeHtml(resizeImage(image, 360))}" alt="${escapeHtml(alt)}" width="180" height="180" loading="lazy">`
                : ''
            }
          </span>
          <span class="kauer-search__product-title">${highlight(product.title, term)}</span>
          <span class="kauer-search__product-price${onSale ? ' kauer-search__product-price--sale' : ''}">
            ${priceVaries ? 'ab ' : ''}${this.#formatMoney(product.price)}
            ${onSale ? `<s>${this.#formatMoney(product.compare_at_price_max)}</s>` : ''}
          </span>
        </a>
      </li>`;
  }
}

if (!customElements.get('kauer-search-bar')) {
  customElements.define('kauer-search-bar', KauerSearchBar);
}
